<template>
  <main id="app">
    <div class="app-container">
      <!-- TopHead is the header with the information about the app -->
      <TopHead v-if="app && messages.length > 0" :app="app"></TopHead>
      <section class="container chat-container">
        <!-- Welcome component is for onboarding experience and language picker -->
        <Welcome
          @submit="send"
          v-if="app && messages.length == 0"
          :app="app"
        ></Welcome>

        <!-- Messages Table -->
        <section class="messages" v-else>
          <div v-for="m in messages" class="message">
            <div v-if="m.selfMessage" class="bubble-wrap">
              <!-- My message -->
              <Bubble :text="m.selfMessage" from="me" />
            </div>

            <!-- Component iterator (Dialogflow Gateway Feature) -->
            <div
              v-else
              class="bubble-wrap"
              v-for="component in m.queryResult.fulfillmentMessages"
            >
              <!-- Default / Webhook bubble -->
              <Bubble
                :text="component.content"
                v-if="component.name == 'DEFAULT'"
              />

              <!-- Simple Response -->
              <Bubble
                :text="
                  component.content.displayText ||
                  component.content.textToSpeech
                "
                v-if="component.name == 'SIMPLE_RESPONSE'"
              />

              <!-- Card -->
              <Card
                :title="component.content.title"
                :subtitle="component.content.subtitle"
                :image="component.content.image"
                :text="component.content.formattedText"
                :buttons="component.content.buttons"
                v-if="component.name == 'CARD'"
              />

              <!-- Carousel layout and cards -->
              <div class="carousel" v-if="component.name == 'CAROUSEL_CARD'">
                <Card
                  v-for="card in component.content"
                  @click.native="send(card.info.key, 'card')"
                  :key="card.info.key"
                  :title="card.title"
                  :image="card.image"
                  :subtitle="card.subtitle"
                  :text="card.description"
                />
              </div>

              <!-- List -->
              <List
                @select="send"
                :items="component.content.items"
                :title="component.content.title"
                v-if="component.name == 'LIST'"
              />

              <!-- Webhook Image -->
              <Picture
                v-if="component.name == 'IMAGE'"
                :image="component.content"
              />
            </div>
          </div>
          <div class="message" v-if="loading && !this.socket">
            <div class="bubble-wrap">
              <!-- Default / Webhook bubble (Loading) -->
              <Bubble loading="true" />
            </div>
          </div>
        </section>
      </section>

      <!-- ChatInput is made for submitting queres and displaying suggestions -->
      <ChatInput
        @submit="send"
        :suggestions="suggestions"
        :intentName="intentName"
        v-if="!app || messages.length != 0"
      ></ChatInput>

      <!-- Audio toggle (on the top right corner), used to toggle the audio output, default mode is defined in the settings -->
      <div
        :aria-label="
          (config.i18n[lang()] && config.i18n[lang()].muteTitle) ||
          config.i18n[config.app.fallback_lang].inputTitle
        "
        :title="
          (config.i18n[lang()] && config.i18n[lang()].muteTitle) ||
          config.i18n[config.app.fallback_lang].inputTitle
        "
        class="audio-toggle"
        @click="muted = !muted"
      >
        <i aria-hidden="true" class="material-icons" v-if="!muted">volume_up</i>
        <i aria-hidden="true" class="material-icons" v-else>volume_off</i>
      </div>
    </div>
  </main>
</template>

<style lang="sass">
@import url('https://fonts.googleapis.com/css?family=Roboto:400,500,700')

body
  margin: 0
  padding: 0
  font-family: Roboto, sans-serif

main
  height: 100vh
  box-sizing: border-box

.app-container
  background-color: white
  display: flex
  flex-direction: column
  height: 100%
  position: relative
  overflow: hidden
    // border-radius: 32px
    // box-shadow: 0 10px 15px -5px rgba(0, 0, 0, 0.4)

.chat-container
  width: 100%
  position: relative
  padding: 0 16px
  box-sizing: border-box
  flex: 1
  overflow-y: auto
  scroll-behavior: smooth

@font-face
  font-family: 'Material Icons'
  font-style: normal
  font-weight: 400
  font-display: swap
  src: url(https://fonts.gstatic.com/s/materialicons/v42/flUhRq6tzZclQEJ-Vdg-IuiaDsNcIhQ8tQ.woff2) format('woff2')

.fab
  top: calc(50% - 9px)

.material-icons
  font-family: 'Material Icons'
  font-weight: normal
  font-style: normal
  font-size: 24px
  line-height: 1
  letter-spacing: normal
  text-transform: none
  display: inline-block
  white-space: nowrap
  word-wrap: normal
  direction: ltr
  -webkit-font-feature-settings: 'liga'
  -webkit-font-smoothing: antialiased
</style>

<style lang="sass" scoped>
.message
  width: 100%
  box-sizing: border-box
  display: flex
  flex-direction: column

.bubble-wrap
  margin-bottom: 12px

.audio-toggle
  position: absolute
  top: 0
  right: 0
  margin: 16px
  z-index: 999
  padding: 10px
  background-color: #F1F3F4
  border-radius: 50%
  width: 24px
  height: 24px
  cursor: pointer
  color: #202124

.carousel
  overflow-x: auto
  overflow-y: hidden
  white-space: nowrap
  -webkit-overflow-scrolling: touch
  box-sizing: border-box
  padding-bottom: 18px
  width: 100%

.carousel .card
  max-width: 300px
</style>

<script>
import Welcome from "./../Welcome/Welcome.vue";
import TopHead from "./../Partials/TopHead.vue";
import ChatInput from "./../Partials/ChatInput.vue";

import Bubble from "./../RichComponents/Bubble.vue";
import Card from "./../RichComponents/Card.vue";
import List from "./../RichComponents/List.vue";
import Picture from "./../RichComponents/Picture.vue";

import { v1 as uuidv1 } from "uuid";
import * as moment from "moment";

var last_interaction = Infinity;
var received_NPS = false;
var called_attendance = false;
var last_intent = "";
var REFRESH_INTERVAL_ID;
const INTERVAL_QUEUE = 3 * 60 * 1000; // 3 mins em ms

export default {
  name: "app",
  components: {
    Welcome,
    TopHead,
    ChatInput,
    Bubble,
    Card,
    List,
    Picture,
  },
  data() {
    return {
      app: null,
      messages: [],
      socket: null,
      language: "",
      session: "",
      muted: this.config.app.muted,
      loading: false,
      position: -1,
      startAttendance: false,
      intentName: "",
    };
  },
  mounted() {
    this.app = this.config.agent;
    if (this.history())
      localStorage.setItem("agent", JSON.stringify(this.config.agent));
    this.scrollDown();

    // Tenta reconectar ao websocket caso a conexão tenha sido
    // interrompida bruscamente
    if (localStorage.getItem("in_attendance") == "true") {
      this.connect(this.config.app.websocket);
    }

    if (localStorage.getItem("STARTATTENDANCE") == "true") {
      this.startAttendance = true;
    }
    // Evento recebido através do iframe
    window.addEventListener("message", this.scrollDown);
  },
  created() {
    /* If history is enabled, the messages are retrieved from localStorage */
    // if (this.history() && localStorage.getItem("message_history") !== null) {
    //   this.messages = JSON.parse(localStorage.getItem("message_history"));
    // }

    /* Session should be persistent (in case of page reload, the context should stay) */
    if (this.history() && localStorage.getItem("session") !== null) {
      this.session = localStorage.getItem("session");
    } else {
      this.session = uuidv1();
      if (this.history()) localStorage.setItem("session", this.session);
    }

    console.log("[DEBUG] SESSION IS", localStorage.getItem("session"));
    /* Cache Agent (this will save bandwith) */
    if (this.history() && localStorage.getItem("agent") !== null) {
      this.app = JSON.parse(localStorage.getItem("agent"));
    }

    const TIME_FOR_NPS = 2 * 60 * 1000; // 2 min;
    setInterval(() => {
      if (
        !received_NPS &&
        !this.socket &&
        Date.now() - TIME_FOR_NPS > last_interaction &&
        !last_intent.toLowerCase().includes("começar")
      ) {
        received_NPS = true;
        let _req = {
          session_id: this.session,
          text: "send_nps",
          template: "default",
          origin: {
            type: "USER",
          },
        };
        this.send_request(_req);
      }
    }, TIME_FOR_NPS);
  },
  computed: {
    /* The code below is used to extract suggestions from last message, to display it on ChatInput */
    suggestions() {
      const lastMessageIsSelf =
        this.messages.length > 0 &&
        this.messages[this.messages.length - 1].selfMessage;
      console.log(lastMessageIsSelf);
      console.log("suggest");
      let atendente = false;
      let lastMsg = this.messages[this.messages.length - 1].queryResult;
      if (lastMsg != undefined && lastMsg.fulfillmentMessages != undefined) {
        atendente = lastMsg.fulfillmentMessages[0].atendente;
      }
      if (this.socket) {
        console.log("\n\n\n[DEBUG SOCKET]", this.socket, "\n\n\n");
        return {
          text_suggestions: this.config.i18n[this.lang()].cancel,
        };
      } else if (this.messages.length > 0 && !lastMessageIsSelf) {
        let last_message = this.messages[this.messages.length - 1].queryResult
          .fulfillmentMessages;
        let suggestions = {};
        console.table(last_message);
        for (let component in last_message) {
          if (last_message[component].name == "SUGGESTIONS")
            suggestions.text_suggestions = last_message[component].content;
          if (last_message[component].name == "LINK_OUT_SUGGESTION")
            suggestions.link_suggestion = last_message[component].content;
        }

        if (!suggestions.text_suggestions) {
          suggestions.text_suggestions =
            this.config.i18n[this.lang()].defaultSuggestions || [];
        }

        return suggestions;
      } else {
        console.log(this.config.app.start_suggestions);
        return {
          text_suggestions: this.config.app.start_suggestions, // <- if no messages are present, return start_suggestions, from config.js to help user figure out what he can do with your application
        };
      }
    },
  },
  watch: {
    /* This function is triggered, when new messages arrive */
    messages(messages) {
      if (this.history())
        localStorage.setItem("message_history", JSON.stringify(messages)); // <- Save history if the feature is enabled
    },
    /* This function is triggered, when request is started or finished */
    loading() {
      this.scrollDown();
    },
  },
  methods: {
    log(m) {
      console.log(m);
    },
    businessHours() {
      const date = moment().utcOffset(-3).format("YYYY-MM-DD");
      const dow = moment().utcOffset(-3).day(); // Dia sa semana contados por 1 até 7

      const start = moment(`${date}T08:30:00-03:00`);
      const end = moment(`${date}T17:30:00-03:00`);
      const now = moment(moment().utcOffset(-3).format());

      const businessHours = now.isBetween(start, end);
      const weekday = dow < 6;

      console.log("Está em horário de trabalho?", businessHours);
      console.log("É dia de semana?", weekday);
      console.log("Que horas são", now);

      return businessHours && weekday;
    },
    addMessage(msg, isFromMe = false, isAtendente = false) {
      let data;
      console.log("msggg");
      console.log(msg);
      if (isFromMe) {
        data = {
          selfMessage: msg,
        };
      } else {
        data = {
          queryResult: {
            fulfillmentMessages: [
              {
                name: "DEFAULT",
                content: msg,
              },
            ],
          },
        };
        if (isAtendente) {
          data.queryResult.fulfillmentMessages[0].atendente = true;
        }
        console.log("datatatata", data);
        this.handle(data);
      }
      this.messages.push(data);
      this.scrollDown();
    },
    scrollDown() {
      requestAnimationFrame(() => {
        let container = document.querySelector("#app .container");
        container.scrollTop = container.scrollHeight;
      });
    },
    connect(socketUrl) {
      console.log(
        "[DEBUG] tentando conectar ao socket",
        socketUrl,
        this.session
      );
      this.socket = new WebSocket(
        socketUrl.replace(":session_id", this.session),
        "echo-protocol"
      );
      this.socket.onopen = (data) => {
        localStorage.setItem("in_attendance", "true");
      };
      this.socket.onerror = (e) => {
        this.addMessage("Não foi possível conectar no momento.");
      };
      this.socket.onmessage = (e) => {
        console.log("ok");
        console.log(e.data);
        const data = e.data;
        const dataObject = JSON.parse(data);
        console.log("lá vem msg");
        console.log(dataObject);
        this.loading = false;

        //Entrou atendente na conversa
        if (
          dataObject.message &&
          dataObject.message.text === "STARTATTENDANCE"
        ) {
          console.log("Entrou atendente na conversa");
          localStorage.setItem("STARTATTENDANCE", "true");
          clearInterval(REFRESH_INTERVAL_ID);
          console.log("\n\n\n\nDEVE PARAR A CONTAGEM\n\n\n\n");
          this.startAttendance = true;
        } else {
          if (dataObject) {
            if (dataObject.message && dataObject.message.text) {
              if (dataObject.message.text.startsWith("ATENDENTE!!!")) {
                this.addMessage(
                  dataObject.message.text.substr(12),
                  false,
                  true
                );
              } else this.addMessage(dataObject.message.text);
              this.scrollDown();
            }
            if (dataObject.unread) {
              dataObject.unread.forEach((msg) => {
                if (msg.text) {
                  if (msg.text.startsWith("ATENDENTE!!!")) {
                    this.addMessage(msg.text.substr(12), false, true);
                  } else this.addMessage(msg.text);
                }
              });
              this.scrollDown();
            }
          }
        }
      };
      this.socket.onclose = (e) => {
        console.log("fechou o socket");
        localStorage.setItem("in_attendance", "false");
        localStorage.setItem("STARTATTENDANCE", "false");
        this.startAttendance = false;
        this.socket = null;
        document.body.onunload = null;
        if (e.code === 1002) {
          this.send("Reativar assistente virtual");
        }
      };
    },
    close() {
      if (this.socket) this.socket.close();
      fetch(this.config.app.cancel, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: this.session, origin: "user" }),
      });
    },
    validate(data) {
      if (data) {
        if (data.queryResult) {
          if (Array.isArray(data.queryResult.fulfillmentMessages)) {
            return true;
          }
        }
      }
      return false;
    },
    send(q, template = "default") {
      this.addMessage(q, true);
      last_interaction = Date.now();
      let request = {
        session_id: this.session,
        text: q,
        template,
        origin: {
          type: "USER",
        },
        ip: null,
      }; // <- this is how a Dialogflow request looks like

      // Envia mensagem pelo socket, caso exista uma conexão ativa
      if (this.socket) {
        this.socket.send(q);
      }

      if (received_NPS && !last_intent.includes("[NPS]")) {
        received_NPS = false;
      }
      // Todas as mensagens são enviadas para o dialogflow,
      // mesmo quando em atendimento
      // para garantir que o app conseguirá receber comandos

      this.loading = true;
      this.send_request(request);
    },
    send_request(req) {
      function getIp(callback) {
        fetch("https://get.geojs.io/v1/ip.json")
          .then((res) => res.json())
          .then((response) => {
            callback(response.ip);
          })
          .catch((data, status) => {
            callback(null);
          });
      }
      getIp((ip) => {
        req.ip = ip;
        fetch(this.config.app.gateway, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(req),
        })
          .then((response) => {
            return response.json();
          })
          .then((res) => {
            if (res.data) {
              this.handle(res.data.response);
            }
          })
          .catch((e) => {
            console.log("error", e);
          });
      });
    },
    requestQueuePosition() {
      let _req = {
        session_id: this.session,
        text: "Atualizar posição da fila de atendimento",
        template: "default",
        origin: {
          type: "USER",
        },
      };
      this.send_request(_req);
    },

    handle(response) {
      let action = response.queryResult.action;
      console.log("Action", action);
      console.log("REFRESH_INTERVAL_ID", REFRESH_INTERVAL_ID);
      console.log("localStorage", localStorage);
      console.log("Called attendance", called_attendance);
      if (
        this.businessHours() &&
        action === "attendance.enable" &&
        !called_attendance
      ) {
        REFRESH_INTERVAL_ID = setInterval(
          this.requestQueuePosition,
          INTERVAL_QUEUE
        );
        called_attendance = true;
        console.log("\n\n\n\nDEVE COMEÇAR A CONTAGEM\n\n\n\n");
      } else if (action === "attendance.disable" || !this.businessHours()) {
        clearInterval(REFRESH_INTERVAL_ID);
        called_attendance = false;
        console.log("\n\n\n\nDEVE PARAR A CONTAGEM\n\n\n\n");
      }
      console.log("HANDLE ACTION\n\n\n", action);
      let shouldDisplayDFMessage = false;
      let intent = response.queryResult.intent;
      this.intentName = intent ? intent.displayName : "";

      console.log("[DEBUG] intentName", this.intentName);
      console.log("[DEBUG] DF response", response.queryResult);
      if (intent != undefined && intent.displayName != undefined) {
        last_intent = response.queryResult.intent.displayName;
      }
      for (let component in response.queryResult.fulfillmentMessages) {
        const messageData = response.queryResult.fulfillmentMessages[component];

        // Support components trigger chat supporthoje
        if (messageData.name === "SUPPORT") {
          shouldDisplayDFMessage = true;
          if (messageData.action === "START") {
            this.connect(this.config.app.websocket);
          } else {
            this.close();
          }
        }

        let text; // <- init a text variable

        /* Set the text variable according to component name */
        if (messageData.name == "DEFAULT") text = messageData.content;
        if (messageData.name == "SIMPLE_RESPONSE")
          text = messageData.content.textToSpeech;

        if (messageData.links && messageData.links.length > 0) {
          messageData.links.forEach(link => {
            const contentMessage = response.queryResult.fulfillmentMessages[component].content;;
            response.queryResult.fulfillmentMessages[component].content = contentMessage.replace(link.link, `<a href='${link.url}' target='_blank'>${link.title}</a>`)
          })
        }

        if (window.SpeechSynthesisUtterance) {
          let speech = new SpeechSynthesisUtterance(text);
          speech.voiceURI = "native"; // <- change this, to get a different voice

          /* This "hack" is used to format our lang format, to some other lang format (example: en -> en_EN). Mainly for Safari, Firefox and Edge */
          speech.lang = this.lang() + "-" + this.lang().toUpperCase();

          if (!this.muted) window.speechSynthesis.speak(speech); // <- if app is not muted, speak out the speech
        }
      }
      if (!this.socket || shouldDisplayDFMessage) {
        this.messages.push(response);
        this.loading = false;
      }
    },
  },
};
</script>
